import type { CSSProperties, FC, ReactNode, RefObject } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  columnDroppableId,
  type KanbanColumnStatus,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';

type LaneRenderArgs = {
  isOver: boolean;
};

export interface KanbanColumnLaneProps {
  status: KanbanColumnStatus;
  /** Only mount useDroppable while a work order is dragged — avoids DnD context re-renders. */
  droppable: boolean;
  scrollParentRef: RefObject<HTMLDivElement | null>;
  children: (args: LaneRenderArgs) => ReactNode;
}

const baseLaneStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  borderRadius: 12,
  padding: 10,
  transition: 'background 120ms ease, border-color 120ms ease',
};

/**
 * Isolates `useDroppable` so the column shell does not subscribe to DnD context
 * except while work-order drops are actually possible.
 */
export const KanbanColumnLane: FC<KanbanColumnLaneProps> = ({
  status,
  droppable,
  scrollParentRef,
  children,
}) => {
  if (!droppable) {
    return (
      <div
        ref={(node) => {
          scrollParentRef.current = node;
        }}
        style={{
          ...baseLaneStyle,
          background: WORK_ORDER_UI.columnLaneBg,
          border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
        }}
      >
        {children({ isOver: false })}
      </div>
    );
  }

  return (
    <KanbanColumnDroppableLane
      status={status}
      scrollParentRef={scrollParentRef}
    >
      {children}
    </KanbanColumnDroppableLane>
  );
};

const KanbanColumnDroppableLane: FC<{
  status: KanbanColumnStatus;
  scrollParentRef: RefObject<HTMLDivElement | null>;
  children: (args: LaneRenderArgs) => ReactNode;
}> = ({ status, scrollParentRef, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: columnDroppableId(status),
    data: { type: 'column', status },
  });

  return (
    <div
      ref={(node) => {
        scrollParentRef.current = node;
        setNodeRef(node);
      }}
      style={{
        ...baseLaneStyle,
        background: isOver ? '#ecfdf5' : WORK_ORDER_UI.columnLaneBg,
        border: isOver
          ? `2px solid ${WORK_ORDER_UI.primary}`
          : `1px solid ${WORK_ORDER_UI.cardBorder}`,
      }}
    >
      {children({ isOver })}
    </div>
  );
};
